import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { css } from '@linaria/core'
import { boolAttr, sanitizeProps } from '@fs/zion-frontend-friends'
import { NoticeImportant, NoticeWarning, NoticeInfo, MenuSettings } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import { colors } from '../theme'
import { Button } from '../buttons'
import { Expander, ExpanderContent } from '../expander'
import { Paragraph } from '../typography'
import Row from '../row'
import { usePageWidth, useContainerWidth } from '../responsive'
import MarginBox from '../margin-box'

const contentCss = css`
  background-color: ${colors.gray00};
`

const containerCss = css`
  border-top: 1px solid ${colors.transparent.gray10};

  &[sticky] {
    position: sticky;
    top: 0;
    z-index: 1;
  }
`

const typeMap = {
  warning: {
    Icon: NoticeWarning,
    iconBackground: colors.yellow40,
  },
  error: {
    Icon: NoticeImportant,
    iconBackground: colors.danger60,
  },
  info: {
    Icon: NoticeInfo,
    iconBackground: colors.blue50,
  },
  maintenance: {
    Icon: MenuSettings,
    iconBackground: colors.yellow40,
  },
}

export default function AlertBanner({
  message,
  type,
  dismissText,
  onDismiss,
  confirmText,
  onConfirm,
  Icon: IconOverride,
  open,
  setOpen,
  confirmProps,
  dismissProps,

  /** [HIDDEN]: For internal Zion-UI use only */
  disableSticky,
  alwaysSticky,
}) {
  const isMdBreakpoint = usePageWidth()({ xl: true })
  const atWidth = useContainerWidth()
  const [expanderOpen, setExpanderOpen] = useState(open)
  const [t] = useTranslation()
  let { iconBackground } = typeMap[type] || {}
  // If IconOverride is defined, but type is not, give it a default help color background
  if (!iconBackground && IconOverride) {
    iconBackground = colors.blue50
  }
  let { Icon } = typeMap[type] || {}
  Icon = IconOverride || Icon

  const onDismissClick = () => {
    if (onDismiss) {
      onDismiss(() => setExpanderOpen(false))
    } else {
      setExpanderOpen(false)
    }
  }

  const onConfirmClick = () => {
    if (onConfirm) {
      onConfirm(() => setExpanderOpen(false))
    } else {
      setExpanderOpen(false)
    }
  }

  const onTransitionEnd = () => setOpen(expanderOpen)

  useEffect(() => {
    if (open) setExpanderOpen(true)
  }, [open])

  // The entire AlertBanner needs to be removed from the dom after its dismissed
  return open ? (
    <div
      id="content-start"
      // Making this a region makes it accessible via screenreader landmarks
      role="region"
      aria-label={t('alertBanner.notice', 'Important Notice')}
      sticky={boolAttr((isMdBreakpoint && !disableSticky) || alwaysSticky)}
      className={containerCss}
    >
      {/* The entire banner is aria-hidden if the banner has been dismissed */}
      <Expander setOpen={setExpanderOpen} open={expanderOpen}>
        <MarginBox as={ExpanderContent} marginX="sm" marginY="xxs" onTransitionEnd={onTransitionEnd}>
          <Row
            // Designating this as a group makes the screen reader tell the user
            // that this interface is all related. When they use the "skip to main content"
            // button, it does not tell them they are in the "Important Notice" region.
            // This is a good way to let them know.
            role="group"
            aria-label={t('alertBanner.notice', 'Important Notice')}
            className={contentCss}
            guttersY="none"
            wrap={atWidth({ default: true, lg: false })}
          >
            <Row alignY="middle" guttersX="xs" growX>
              {/* GRAPHIC  */}
              {Icon && <Icon size="sm" color="white" background={iconBackground} noShrinkX />}

              {/* MESSAGE */}
              <Paragraph data-full-width size="sm" alignY="middle" growX>
                {message}
              </Paragraph>
            </Row>

            {/* BUTTONS */}
            <Row alignX="end" alignY="middle" guttersY="none" noShrinkX growX>
              {dismissText && (
                <Button onClick={onDismissClick} {...sanitizeProps(dismissProps)} Icon={undefined}>
                  {dismissText}
                </Button>
              )}
              {confirmText && (
                <Button onClick={onConfirmClick} {...sanitizeProps(confirmProps)} Icon={undefined}>
                  {confirmText}
                </Button>
              )}
            </Row>
          </Row>
        </MarginBox>
      </Expander>
    </div>
  ) : null
}

function requireAtLeastOneProp(props, propName, componentName) {
  const { dismissText, confirmText } = props
  if (!dismissText && !confirmText) {
    return new Error(`Either dismissText or confirmText is required for ${componentName}`)
  }
  return null
}

AlertBanner.propTypes = {
  /** Determines the color and icon (if one is not provided) */
  type: PropTypes.oneOf(['warning', 'error', 'maintenance', 'info', 'none']),

  /** The text to display in the banner. JSX is permitted only to support inline Links.  No other use case is allowed. */
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,

  /** The icon to display. Overrides the default icon provided by type. */
  Icon: PropTypes.elementType,

  /** Sets the text of the dimsiss button. This or the confirmText is required. */
  dismissText: requireAtLeastOneProp,

  /** Sets the text of the confirm button This or the dismissText is required. */
  confirmText: requireAtLeastOneProp,

  /** Callback for confirm button press. Closes banner by default. */
  onConfirm: PropTypes.func,

  /** Callback for confirm button press. Closes banner by default. */
  onDismiss: PropTypes.func,

  /** If true, the banner will be visible. This should be provided from a useState(). */
  open: PropTypes.bool.isRequired,

  /** Sets open to true/false. This should be provided from a useState(). */
  setOpen: PropTypes.func.isRequired,

  /** Props that will be passed to the confirm button. Icon is not allowed here */
  confirmProps: PropTypes.object,

  /** Props that will be passed to the dismiss button. Icon is not allowed here */
  dismissProps: PropTypes.object,

  /** [HIDDEN]: If true, the banner will always be sticky, even on mobile. */
  /* alwaysSticky: PropTypes.bool, */

  /** [HIDDEN]: Disables sticky behavior on both mobile and desktop. */
  /* disableSticky: PropTypes.bool, */
}
